import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Grid,
  Card,
  CardContent,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  Avatar,
  LinearProgress,
  MenuItem,
  TextField,
  Alert
} from '@mui/material';
import {
  Grade as GradeIcon,
  TrendingUp as TrendingUpIcon,
  TrendingDown as TrendingDownIcon,
  School as SchoolIcon,
  Download as DownloadIcon,
  Assessment as AssessmentIcon
} from '@mui/icons-material';
import { motion } from 'framer-motion';
import { useAuth } from '../../contexts/AuthContext';
import axios from 'axios';
import { useSnackbar } from 'notistack';

const ExamResults = () => {
  const { user } = useAuth();
  const { enqueueSnackbar } = useSnackbar();
  const [examResults, setExamResults] = useState([]);
  const [loading, setLoading] = useState(false);
  const [selectedExam, setSelectedExam] = useState('all');
  const [stats, setStats] = useState({
    totalExams: 0,
    averageGrade: 0,
    highestScore: 0,
    lowestScore: 0,
    passedExams: 0,
    failedExams: 0
  });

  useEffect(() => {
    fetchExamResults();
  }, []);

  const fetchExamResults = async () => {
    try {
      setLoading(true);
      const response = await axios.get('student-portal/exam-results');
      if (response.data.success) {
        setExamResults(response.data.data);
        calculateStats(response.data.data);
      }
    } catch (error) {
      enqueueSnackbar('Failed to fetch exam results', { variant: 'error' });
    } finally {
      setLoading(false);
    }
  };

  const calculateStats = (results) => {
    if (results.length === 0) return;

    const scores = results.map(r => parseFloat(r.marks_obtained));
    const totalMarks = results.map(r => parseFloat(r.total_marks));
    const percentages = results.map((r, i) => (scores[i] / totalMarks[i]) * 100);

    const stats = {
      totalExams: results.length,
      averageGrade: percentages.reduce((a, b) => a + b, 0) / percentages.length,
      highestScore: Math.max(...percentages),
      lowestScore: Math.min(...percentages),
      passedExams: results.filter(r => (parseFloat(r.marks_obtained) / parseFloat(r.total_marks)) * 100 >= 40).length,
      failedExams: results.filter(r => (parseFloat(r.marks_obtained) / parseFloat(r.total_marks)) * 100 < 40).length
    };

    setStats(stats);
  };

  const getGrade = (percentage) => {
    if (percentage >= 90) return { grade: 'A+', color: '#4CAF50' };
    if (percentage >= 80) return { grade: 'A', color: '#8BC34A' };
    if (percentage >= 70) return { grade: 'B+', color: '#CDDC39' };
    if (percentage >= 60) return { grade: 'B', color: '#FFC107' };
    if (percentage >= 50) return { grade: 'C+', color: '#FF9800' };
    if (percentage >= 40) return { grade: 'C', color: '#FF5722' };
    return { grade: 'F', color: '#F44336' };
  };

  const downloadGradeSheet = (examId) => {
    window.open(`/api/student-portal/grade-sheet/${examId}`, '_blank');
  };

  const StatCard = ({ title, value, icon, color, subtitle, trend }) => (
    <motion.div
      whileHover={{ scale: 1.02 }}
      transition={{ duration: 0.2 }}
    >
      <Card sx={{ 
        background: `linear-gradient(135deg, ${color}20 0%, ${color}10 100%)`,
        border: `1px solid ${color}30`,
        height: '100%'
      }}>
        <CardContent>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <Box>
              <Typography variant="h4" sx={{ fontWeight: 'bold', color: color }}>
                {typeof value === 'number' ? value.toFixed(1) : value}
                {title.includes('Grade') || title.includes('Score') ? '%' : ''}
              </Typography>
              <Typography variant="body2" color="textSecondary">
                {title}
              </Typography>
              {subtitle && (
                <Box sx={{ display: 'flex', alignItems: 'center', mt: 0.5 }}>
                  {trend && (trend > 0 ? <TrendingUpIcon color="success" fontSize="small" /> : <TrendingDownIcon color="error" fontSize="small" />)}
                  <Typography variant="caption" color="textSecondary" sx={{ ml: 0.5 }}>
                    {subtitle}
                  </Typography>
                </Box>
              )}
            </Box>
            <Avatar sx={{ bgcolor: color, width: 56, height: 56 }}>
              {icon}
            </Avatar>
          </Box>
        </CardContent>
      </Card>
    </motion.div>
  );

  const filteredResults = selectedExam === 'all' 
    ? examResults 
    : examResults.filter(result => result.exam_type === selectedExam);

  return (
    <Box sx={{ p: 3 }}>
      <Typography variant="h4" sx={{ mb: 3, fontWeight: 'bold' }}>
        Exam Results
      </Typography>

      {loading && <LinearProgress sx={{ mb: 2 }} />}

      {/* Stats Cards */}
      <Grid container spacing={3} sx={{ mb: 4 }}>
        <Grid item xs={12} sm={6} md={3}>
          <StatCard 
            title="Total Exams" 
            value={stats.totalExams} 
            icon={<AssessmentIcon />} 
            color="#2196F3"
            subtitle="This academic year"
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <StatCard 
            title="Average Grade" 
            value={parseFloat(stats.averageGrade || 0).toFixed(2)} 
            icon={<GradeIcon />} 
            color="#4CAF50"
            subtitle={`Grade: ${getGrade(stats.averageGrade).grade}`}
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <StatCard 
            title="Highest Score" 
            value={parseFloat(stats.highestScore || 0).toFixed(2)} 
            icon={<TrendingUpIcon />} 
            color="#FF9800"
            subtitle="Best performance"
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <StatCard 
            title="Pass Rate" 
            value={stats.totalExams > 0 ? ((stats.passedExams / stats.totalExams) * 100).toFixed(2) : '0.00'} 
            icon={<SchoolIcon />} 
            color="#9C27B0"
            subtitle={`${stats.passedExams}/${stats.totalExams} passed`}
          />
        </Grid>
      </Grid>

      {/* Filter and Results */}
      <Card>
        <CardContent>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
            <Typography variant="h6">Exam Results</Typography>
            <Box sx={{ display: 'flex', gap: 2 }}>
              <TextField
                select
                size="small"
                label="Filter by Exam Type"
                value={selectedExam}
                onChange={(e) => setSelectedExam(e.target.value)}
                sx={{ minWidth: 200 }}
              >
                <MenuItem value="all">All Exams</MenuItem>
                <MenuItem value="midterm">Midterm</MenuItem>
                <MenuItem value="final">Final</MenuItem>
                <MenuItem value="quiz">Quiz</MenuItem>
                <MenuItem value="assignment">Assignment</MenuItem>
              </TextField>
              <Button 
                variant="contained" 
                startIcon={<DownloadIcon />}
                onClick={() => window.open('/api/student-portal/all-results-pdf', '_blank')}
              >
                Download All Results
              </Button>
            </Box>
          </Box>
          
          <TableContainer>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>Subject</TableCell>
                  <TableCell>Exam Type</TableCell>
                  <TableCell>Date</TableCell>
                  <TableCell>Marks</TableCell>
                  <TableCell>Percentage</TableCell>
                  <TableCell>Grade</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell>Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {filteredResults.map((result) => {
                  const percentage = (parseFloat(result.marks_obtained) / parseFloat(result.total_marks)) * 100;
                  const gradeInfo = getGrade(percentage);
                  const passed = percentage >= 40;
                  
                  return (
                    <TableRow key={result.result_id}>
                      <TableCell>
                        <Typography variant="body2" fontWeight="bold">
                          {result.subject_name}
                        </Typography>
                        <Typography variant="caption" color="textSecondary">
                          {result.exam_name}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Chip 
                          label={result.exam_type} 
                          size="small"
                          color="primary"
                          variant="outlined"
                        />
                      </TableCell>
                      <TableCell>
                        {new Date(result.exam_date).toLocaleDateString()}
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2">
                          {result.marks_obtained}/{result.total_marks}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body1" fontWeight="bold">
                          {percentage.toFixed(1)}%
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Chip 
                          label={gradeInfo.grade}
                          sx={{ 
                            bgcolor: gradeInfo.color, 
                            color: 'white',
                            fontWeight: 'bold'
                          }}
                          size="small"
                        />
                      </TableCell>
                      <TableCell>
                        <Chip 
                          label={passed ? 'Pass' : 'Fail'}
                          color={passed ? 'success' : 'error'}
                          size="small"
                        />
                      </TableCell>
                      <TableCell>
                        <Button
                          size="small"
                          startIcon={<DownloadIcon />}
                          onClick={() => downloadGradeSheet(result.exam_id)}
                        >
                          Grade Sheet
                        </Button>
                      </TableCell>
                    </TableRow>
                  );
                })}
                {filteredResults.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={8} align="center">
                      <Alert severity="info">
                        {selectedExam === 'all' 
                          ? 'No exam results available yet'
                          : `No ${selectedExam} results available`
                        }
                      </Alert>
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </CardContent>
      </Card>

      {/* Performance Analysis */}
      {examResults.length > 0 && (
        <Card sx={{ mt: 3 }}>
          <CardContent>
            <Typography variant="h6" sx={{ mb: 2 }}>Performance Analysis</Typography>
            <Grid container spacing={2}>
              <Grid item xs={12} md={6}>
                <Box sx={{ p: 2, bgcolor: 'background.paper', borderRadius: 2 }}>
                  <Typography variant="subtitle1" fontWeight="bold" gutterBottom>
                    Strengths
                  </Typography>
                  {examResults
                    .filter(r => (parseFloat(r.marks_obtained) / parseFloat(r.total_marks)) * 100 >= 80)
                    .slice(0, 3)
                    .map((result, index) => (
                      <Typography key={index} variant="body2" color="success.main">
                        • {result.subject_name}: {((parseFloat(result.marks_obtained) / parseFloat(result.total_marks)) * 100).toFixed(1)}%
                      </Typography>
                    ))
                  }
                  {examResults.filter(r => (parseFloat(r.marks_obtained) / parseFloat(r.total_marks)) * 100 >= 80).length === 0 && (
                    <Typography variant="body2" color="textSecondary">
                      Keep working to identify your strengths!
                    </Typography>
                  )}
                </Box>
              </Grid>
              <Grid item xs={12} md={6}>
                <Box sx={{ p: 2, bgcolor: 'background.paper', borderRadius: 2 }}>
                  <Typography variant="subtitle1" fontWeight="bold" gutterBottom>
                    Areas for Improvement
                  </Typography>
                  {examResults
                    .filter(r => (parseFloat(r.marks_obtained) / parseFloat(r.total_marks)) * 100 < 60)
                    .slice(0, 3)
                    .map((result, index) => (
                      <Typography key={index} variant="body2" color="warning.main">
                        • {result.subject_name}: {((parseFloat(result.marks_obtained) / parseFloat(result.total_marks)) * 100).toFixed(1)}%
                      </Typography>
                    ))
                  }
                  {examResults.filter(r => (parseFloat(r.marks_obtained) / parseFloat(r.total_marks)) * 100 < 60).length === 0 && (
                    <Typography variant="body2" color="success.main">
                      Great job! All subjects are performing well.
                    </Typography>
                  )}
                </Box>
              </Grid>
            </Grid>
          </CardContent>
        </Card>
      )}
    </Box>
  );
};

export default ExamResults;
